import React, { useState } from 'react';
import { SettingsLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  Badge,
  Button,
  FormSection,
} from '../../../design-system/components';
import { test_connection_admin_settings_paypal_settings_path } from '../../../lib/routes';
import { CheckCircle, XCircle, Info } from 'lucide-react';

export default function Paypal({ settings }) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  async function handleTestConnection() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(test_connection_admin_settings_paypal_settings_path(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      const data = await res.json();
      setTestResult(data);
    } catch {
      setTestResult({ success: false, message: 'Network error. Please try again.' });
    } finally {
      setTesting(false);
    }
  }

  return (
    <SettingsLayout>
      <PageHeader
        title="PayPal Settings"
        description="PayPal Payouts API credentials for automated affiliate payments."
      />

      <div className="mt-6 space-y-8">
        <FormSection
          heading="API Credentials"
          description="PayPal credentials are configured via environment variables. Update PAYPAL_CLIENT_ID and PAYPAL_CLIENT_SECRET in your server environment."
        >
          <div className="max-w-md space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-edge bg-surface-secondary px-4 py-3">
              <span className="text-sm font-medium text-fg">Environment</span>
              <Badge variant={settings.mode === 'production' ? 'warning' : 'info'}>
                {settings.mode}
              </Badge>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-edge bg-surface-secondary px-4 py-3">
              <span className="text-sm font-medium text-fg">Client ID</span>
              {settings.client_id_present ? (
                <code className="text-sm text-fg-muted">{settings.client_id_masked}</code>
              ) : (
                <Badge variant="danger">Not set</Badge>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg border border-edge bg-surface-secondary px-4 py-3">
              <span className="text-sm font-medium text-fg">Client Secret</span>
              {settings.secret_present ? (
                <code className="text-sm text-fg-muted">••••••••••••</code>
              ) : (
                <Badge variant="danger">Not set</Badge>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg border border-edge bg-surface-secondary px-4 py-3">
              <span className="text-sm font-medium text-fg">Status</span>
              {settings.configured ? (
                <Badge variant="success">Configured</Badge>
              ) : (
                <Badge variant="danger">Not configured</Badge>
              )}
            </div>
          </div>
        </FormSection>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleTestConnection}
            loading={testing}
            disabled={!settings.configured}
          >
            Test Connection
          </Button>
        </div>

        {testResult && (
          <div
            className={`flex items-center gap-2 text-sm ${testResult.success ? 'text-success-600' : 'text-danger-600'}`}
          >
            {testResult.success ? (
              <CheckCircle size={16} />
            ) : (
              <XCircle size={16} />
            )}
            {testResult.message}
          </div>
        )}

        <div className="flex items-start gap-2 rounded-lg border border-edge bg-surface-secondary px-4 py-3 text-sm text-fg-muted">
          <Info size={16} className="mt-0.5 shrink-0" />
          <span>
            To update PayPal credentials, set the <code className="font-mono text-fg">PAYPAL_CLIENT_ID</code>,{' '}
            <code className="font-mono text-fg">PAYPAL_CLIENT_SECRET</code> environment variables on your server and restart the application. PayPal mode is automatic
            based on Rails environment (sandbox in development, production in production).
          </span>
        </div>
      </div>
    </SettingsLayout>
  );
}
